/**
 * UsageAlertBanner — Shows remaining usage for a gated action
 * Displays contextual alerts: green (plenty left), amber (running low), red (limit reached)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, LinearProgress, Chip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { useGate } from '../../hooks/useGate';
import { USAGE_ALERT_TEXTS, ACTION_LABELS, getGuestUsage } from '../../constants/gateConfig';

const UsageAlertBanner = ({ actionCode, language = 'en' }) => {
  const navigate = useNavigate();
  const { peekUsage, isAuthenticated } = useGate();
  const usage = peekUsage(actionCode);
  const txt = USAGE_ALERT_TEXTS[language] || USAGE_ALERT_TEXTS.en;
  const actionLabel = (ACTION_LABELS[language] || ACTION_LABELS.en)[actionCode] || actionCode;

  // Don't show for unlimited (paid) users
  if (usage.isUnlimited) return null;

  // Don't show for blocked features with 0 limit (handled by GateModal)
  if (usage.limit === 0) return null;

  const { used, limit, remaining } = usage;
  const percent = limit > 0 ? ((used / limit) * 100) : 0;
  const isLow = remaining <= 1 && remaining > 0;
  const isExhausted = remaining <= 0;

  // Calculate hours until reset for guest users
  const getResetHours = () => {
    try {
      const guestData = getGuestUsage();
      if (guestData.resetAt) {
        const elapsed = Date.now() - guestData.resetAt;
        const hoursLeft = Math.max(1, Math.ceil((24 * 60 * 60 * 1000 - elapsed) / (60 * 60 * 1000)));
        return hoursLeft;
      }
    } catch { /* ignore */ }
    return 24;
  };

  const getMessage = () => {
    if (isExhausted) {
      if (!isAuthenticated) {
        return txt.limitReachedGuest.replace('{action}', actionLabel);
      }
      return txt.limitReached
        .replace('{action}', actionLabel)
        .replace('{hours}', getResetHours());
    }
    if (isLow) {
      return txt.lastOne.replace('{action}', actionLabel);
    }
    return txt.remaining
      .replace('{remaining}', remaining)
      .replace('{limit}', limit)
      .replace('{action}', actionLabel);
  };

  const getSeverity = () => {
    if (isExhausted) return 'error';
    if (isLow) return 'warning';
    return 'info';
  };

  const getIcon = () => {
    if (isExhausted) return <ErrorOutlineIcon fontSize="small" />;
    if (isLow) return <WarningAmberIcon fontSize="small" />;
    return <InfoOutlinedIcon fontSize="small" />;
  };

  const getColor = () => {
    if (isExhausted) return { bg: '#FFF5F5', border: '#FEB2B2', text: '#C53030', bar: '#E53E3E' };
    if (isLow) return { bg: '#FFFAF0', border: '#FBD38D', text: '#C05621', bar: '#ED8936' };
    return { bg: '#EBF8FF', border: '#BEE3F8', text: '#2B6CB0', bar: '#4299E1' };
  };

  const colors = getColor();

  const handleAction = () => {
    if (!isAuthenticated) {
      navigate('/auth?mode=signup');
    } else {
      navigate('/app', { state: { view: 'account' } });
    }
  };

  return (
    <Box
      sx={{
        bgcolor: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
        p: 1.5,
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Box sx={{ color: colors.text, display: 'flex', flexShrink: 0 }}>
            {getIcon()}
          </Box>
          <Typography variant="caption" sx={{ color: colors.text, fontWeight: 500, lineHeight: 1.3 }}>
            {getMessage()}
          </Typography>
        </Box>
        {(isExhausted || isLow) && (
          <Button
            size="small"
            variant="outlined"
            onClick={handleAction}
            sx={{
              fontSize: '0.7rem',
              py: 0.25,
              px: 1,
              minWidth: 'auto',
              borderColor: colors.border,
              color: colors.text,
              flexShrink: 0,
              '&:hover': {
                borderColor: colors.text,
                bgcolor: 'transparent',
              },
            }}
          >
            {!isAuthenticated ? txt.signUp : txt.upgrade}
          </Button>
        )}
      </Box>
      {/* Progress bar showing usage */}
      {limit > 0 && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, percent)}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: `${colors.border}80`,
              '& .MuiLinearProgress-bar': {
                bgcolor: colors.bar,
                borderRadius: 2,
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.25 }}>
            <Typography variant="caption" sx={{ color: colors.text, fontSize: '0.65rem', opacity: 0.8 }}>
              {used}/{limit}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UsageAlertBanner;
